import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Download, Loader2 } from 'lucide-react';
import Pagination from '@/components/ui/pagination';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { userService } from "../../_services";

function ActiveInactiveRefs({ ActiveRepList, InactiveRepList, text }) {
    const [activeJson, setActiveJson] = useState(ActiveRepList || []);
    const [inActiveJson, setInActiveJson] = useState(InactiveRepList || []);
    const [error, setError] = useState('');
    const [apiCalled, setApiCalled] = useState(false);
    const [noDataDisplay, setNoDataDisplay] = useState('');
    const [activeTab, setActiveTab] = useState('active');
    const [activePageOfItems, setActivePageOfItems] = useState([]);
    const [inActivePageOfItems, setInActivePageOfItems] = useState([]);

    useEffect(() => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const userid = Usermeta_Data.split(':')[0];
        getReps(userid);
    }, []);

    const getReps = async (UserId) => {
        try {
            // PRESERVE EXACT API CALL - DO NOT CHANGE
            const apidata = await userService.GetUserReps(UserId);
            setApiCalled(true);
            
            if (apidata != null && apidata.length > 0) {
                try {
                    apidata.forEach((item) => {
                        setActiveJson(JSON.parse(item.activeJson));
                        setInActiveJson(JSON.parse(item.inActiveJson));
                        setNoDataDisplay('Reps not available');
                    });
                } catch (error) {
                    console.error('Error parsing reps data:', error);
                }
            } else {
                setActiveJson([]);
                setInActiveJson([]);
            }
        } catch (error) {
            console.error('Error fetching reps:', error);
            setError('Failed to load reps data');
        }
    };

    const exportReps = async () => {
        try {
            const headingData = ["Name", "Email", "Assigned Group", "Commission"];
            const tableData_Active = [headingData];
            const tableData_Inactive = [headingData];

            // Process active reps
            for (let i = 0; i < activeJson.length; i++) {
                for (let j = 0; j < activeJson[i]["GroupList"].length; j++) {
                    const rowData = [];
                    if (j > 0) {
                        rowData.push('');
                        rowData.push('');
                        rowData.push(activeJson[i]["GroupList"][j]["Group"].split(' ')[0]);
                        rowData.push(activeJson[i]["GroupList"][j]["Group"].split('(')[1].replace(')', ''));
                    } else {
                        rowData.push(activeJson[i]["SubDisName"]);
                        rowData.push(activeJson[i]["SubDisEmail"]);
                        rowData.push(activeJson[i]["GroupList"][j]["Group"].split(' ')[0]);
                        rowData.push(activeJson[i]["GroupList"][j]["Group"].split('(')[1].replace(')', ''));
                    }
                    tableData_Active.push(rowData);
                }
            }

            // Process inactive reps
            for (let i = 0; i < inActiveJson.length; i++) {
                for (let j = 0; j < inActiveJson[i]["GroupList"].length; j++) {
                    const rowData = [];
                    if (j > 0) {
                        rowData.push('');
                        rowData.push('');
                        rowData.push(inActiveJson[i]["GroupList"][j]["Group"].split(' ')[0]);
                        rowData.push(inActiveJson[i]["GroupList"][j]["Group"].split('(')[1].replace(')', ''));
                    } else {
                        rowData.push(inActiveJson[i]["SubDisName"]);
                        rowData.push(inActiveJson[i]["SubDisEmail"]);
                        rowData.push(inActiveJson[i]["GroupList"][j]["Group"].split(' ')[0]);
                        rowData.push(inActiveJson[i]["GroupList"][j]["Group"].split('(')[1].replace(')', ''));
                    }
                    tableData_Inactive.push(rowData);
                }
            }

            const workbook = new ExcelJS.Workbook();
            workbook.addWorksheet('Active_Reps').addRows(tableData_Active);
            workbook.addWorksheet('Inactive_Reps').addRows(tableData_Inactive);
            const buffer = await workbook.xlsx.writeBuffer();
            saveAs(
                new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' }),
                'Active_Inactive_Reps.xlsx'
            );
        } catch (error) {
            console.error('Error exporting reps:', error);
        }
    };

    // fullData  = complete list (activeJson / inActiveJson) — used for pagination calculation
    // pageItems = current page slice from Pagination — used for display (falls back to fullData)
    const renderRepTable = (fullData, pageItems, isActive) => {
        if (!fullData || fullData.length === 0) {
            return (
                <div className="text-center py-8 text-muted-foreground">
                    {noDataDisplay || 'Reps not available'}
                </div>
            );
        }

        const displayData = pageItems.length > 0 ? pageItems : fullData;

        return (
            <>
                <div className="overflow-x-auto border-t">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>E-mail</TableHead>
                            <TableHead>Assigned Group</TableHead>
                            <TableHead>Commission</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {displayData.map((item, index) => (
                            <TableRow key={`${isActive ? 'Active' : 'Inactive'}-${index}`}>
                                <TableCell>{item.SubDisName}</TableCell>
                                <TableCell>{item.SubDisEmail}</TableCell>
                                <TableCell>
                                    {item.GroupList.map((gpitem, _index) => (
                                        <div key={`Group-${index}-${_index}`}>
                                            {gpitem.Group.split(' ')[0]}
                                        </div>
                                    ))}
                                </TableCell>
                                <TableCell>
                                    {item.GroupList.map((gpitem, _index) => (
                                        <div key={`Commission-${index}-${_index}`}>
                                            {gpitem.Group.split('(')[1].replace(')', '')}
                                        </div>
                                    ))}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                </div>
                {/* Always pass the FULL dataset so Pagination doesn't re-paginate an already-paged slice */}
                <div className="px-4 sm:px-6 py-4 border-t">
                <Pagination 
                    items={fullData} 
                    onChangePage={isActive ? setActivePageOfItems : setInActivePageOfItems} 
                />
                </div>
            </>
        );
    };

    const totalReps = activeJson.length + inActiveJson.length;

    if (totalReps === 0 && !apiCalled) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center p-4 py-12 sm:p-6 sm:py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-accent" />
                </CardContent>
            </Card>
        );
    }

    if (totalReps === 0) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Active & Inactive Reps</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-center py-8 text-muted-foreground">No reps found</div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle>Active & Inactive Reps</CardTitle>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={exportReps}
                        className="gap-2"
                    >
                        <Download className="h-4 w-4" />
                        Download Report
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="px-0 sm:px-0">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <div className="px-4 sm:px-6">
                        <TabsList className="w-full">
                            <TabsTrigger value="active" className="flex-1">
                                Active
                            </TabsTrigger>
                            <TabsTrigger value="inactive" className="flex-1">
                                Inactive
                            </TabsTrigger>
                        </TabsList>
                    </div>

                    <TabsContent value="active" className="mt-4">
                        <div className="mb-4 px-4 sm:px-6">
                            <h3 className="text-lg font-semibold mb-1">
                                Total Active Reps: {activeJson.length}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Reps with greater than 1 claim in last 30 days
                            </p>
                        </div>
                        {renderRepTable(activeJson, activePageOfItems, true)}
                    </TabsContent>

                    <TabsContent value="inactive" className="mt-4">
                        <div className="mb-4 px-4 sm:px-6">
                            <h3 className="text-lg font-semibold mb-1">
                                Total Inactive Reps: {inActiveJson.length}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Reps with less than 1 claim in last 30 days
                            </p>
                        </div>
                        {renderRepTable(inActiveJson, inActivePageOfItems, false)}
                    </TabsContent>
                </Tabs>
            </CardContent>
        </Card>
    );
}

export default ActiveInactiveRefs;

